/**
 * Contenu éditable de la page /cv.
 *
 * Source unique : le CV PDF de Marie Mercier (public/CV_Marie_MERCIER.pdf).
 * Rien n'est inventé : si une donnée ne figure pas sur le CV, elle n'apparaît
 * pas ici. Pour modifier la page, il suffit d'éditer ce fichier.
 */

import { projects } from './projectsData';
import bischoff1 from '../assets/projects/bischoff1.jpg';
import lescale2 from '../assets/projects/lescalebeaute2.png';
import lisaRgb1 from '../assets/projects/LisaRgb1.png';

export const CV_FILE = process.env.PUBLIC_URL + '/CV_Marie_MERCIER.pdf';

export const profile = {
  name: 'Marie Mercier',
  role: 'Chargée de communication',
  // Accroche courte : elle doit donner envie de lire la suite, pas la résumer.
  positioning:
    'Huit ans que je donne un visage et une voix aux structures qui en manquent. Campagnes, journaux, affiches, vidéos, réseaux sociaux : je conçois, je mets en page et je pilote jusqu’à l’impression.',
  status: {
    label: 'Actuellement',
    value: 'Fondation de la maison du Diaconat de Mulhouse',
  },
  experienceYears: '8 ans d’expérience',
  location: 'Riedisheim, Alsace',
  languages: 'Français',
  photo: process.env.PUBLIC_URL + '/marie.png',
  photoAlt: 'Portrait de Marie Mercier',
  ctas: [
    { label: 'Télécharger le CV', href: CV_FILE, variant: 'primary', download: true },
    { label: 'Me contacter', href: '#contact', variant: 'ghost' },
  ],
};

/** Liens de la barre de navigation (ancres de la page). */
export const navSections = [
  { id: 'experiences', label: 'Expériences' },
  { id: 'realisations', label: 'Réalisations' },
  { id: 'competences', label: 'Compétences' },
  { id: 'formation', label: 'Formation' },
  { id: 'contact', label: 'Contact' },
];

/* Compétences transverses uniquement : ce que je sais faire, indépendamment
   de l'employeur. Les détails propres à un poste vivent dans `experience`. */
export const skillGroups = [
  {
    title: 'Communication',
    icon: 'megaphone',
    items: [
      'Création de campagnes publicitaires',
      'Rédaction et diffusion de communiqués de presse',
      'Collecte d’articles et travail éditorial',
      'Communication interne et externe',
      'Organisation d’événements',
    ],
  },
  {
    title: 'Direction artistique',
    icon: 'pen',
    items: [
      'Création d’identités visuelles',
      'Création d’affiches et de flyers',
      'Mise en page et édition',
      'Illustration digitale',
      'Gestion de projet',
    ],
  },
  {
    title: 'Print et production',
    icon: 'printer',
    items: [
      'Création de supports de communication print',
      'Mise en page de journaux et de rapports d’activité',
      'Mise en page de magazines et de programmes',
      'Chaîne graphique et préparation des fichiers',
    ],
  },
  {
    title: 'Digital et vidéo',
    icon: 'monitor',
    items: [
      'Création de posts et de stories pour les réseaux sociaux',
      'Réalisation de vidéos d’information',
      'Création de site internet en lien avec un développeur',
      'Mise à jour de site internet',
    ],
  },
];

/** Boîte à outils du CV, dans le même ordre. */
export const software = [
  { name: 'InDesign', abbr: 'Id', level: 'maîtrise', color: '#cc5068' },
  { name: 'Illustrator', abbr: 'Ai', level: 'maîtrise', color: '#df954c' },
  { name: 'Photoshop', abbr: 'Ps', level: 'maîtrise', color: '#5c8fbd' },
  { name: 'Première Pro', abbr: 'Pr', level: 'notions', color: '#8185b0' },
  { name: 'After Effects', abbr: 'Ae', level: 'notions', color: '#8185b0' },
];

export const experience = [
  {
    role: 'Chargée de communication',
    org: 'Fondation de la maison du Diaconat de Mulhouse',
    contract: 'Salariée',
    period: '2020 à aujourd’hui',
    current: true,
    bullets: [
      'Création de supports de communication print pour les 85 établissements',
      'Collecte des articles et mise en page du « Diac’infos », deux numéros par an',
      'Rédaction et diffusion de communiqués de presse',
      'Collecte des informations et mise en page du rapport d’activité',
      'Réalisation de vidéos d’information',
      'Organisation d’événements : rétrospective, cérémonie des vœux',
      'Création de posts et de stories pour les réseaux sociaux',
    ],
    tags: ['Print', 'Édition', 'Relations presse', 'Vidéo', 'Événementiel', 'Réseaux sociaux'],
  },
  {
    role: 'Directrice artistique',
    org: 'En freelance',
    contract: 'Indépendante',
    period: '2017 à aujourd’hui',
    current: true,
    bullets: [
      'Création d’identités visuelles',
      'Création d’affiches et de flyers',
      'Échanges avec les clients',
      'Gestion de projets, gestion administrative et comptabilité',
    ],
    tags: ['Identité visuelle', 'Affiche', 'Flyer', 'Relation client', 'Gestion de projet'],
  },
  {
    role: 'Chargée de communication',
    org: 'Maison de la Famille, Colmar',
    contract: 'Salariée',
    period: '2017 à 2020',
    bullets: [
      'Création de campagnes publicitaires',
      'Création de supports de communication print',
      'Vente de berceaux d’entreprise',
      'Création du site internet en lien avec un développeur',
      'Mise à jour du site internet',
      'Création de posts et de stories pour les réseaux sociaux',
    ],
    tags: ['Campagnes', 'Print', 'Site internet', 'Réseaux sociaux'],
  },
  {
    role: 'Pôle design graphique',
    org: 'Agence CACTUS',
    contract: 'Stage',
    period: 'Juillet à août 2017',
    bullets: [
      'Mise en page d’un programme de spectacles',
      'Création de campagnes publicitaires',
      'Créations de logos et d’identités visuelles',
    ],
    tags: ['Mise en page', 'Logo', 'Identité visuelle'],
  },
  {
    role: 'Direction artistique',
    org: 'Conseil départemental du Haut-Rhin',
    contract: 'Stage',
    period: 'Juin à juillet 2016',
    bullets: [
      'Mise en page du magazine le MAG’',
      'Création d’affiches et de flyers',
    ],
    tags: ['Édition', 'Magazine', 'Affiche'],
  },
];

/**
 * Carrousel « Mes réalisations ».
 * Les projets viennent du portfolio (hors Oxane), avec une seule image chacun
 * et une accroche courte. Le reste (contexte, rôle, année, couleurs) reste
 * synchronisé avec projectsData pour éviter deux vérités.
 */
const SHOWCASE = [
  {
    id: 1,
    image: bischoff1,
    blurb:
      'Un logo où les initiales O et B forment une paire de lunettes, décliné sur tous les supports de la boutique.',
  },
  {
    id: 2,
    image: lescale2,
    blurb:
      'Un univers végétal et apaisant, et un monogramme où une feuille se fond dans les lettres E et B.',
  },
  {
    id: 3,
    image: lisaRgb1,
    blurb:
      'Un logo épuré construit autour de son nom, légèrement encerclé pour évoquer l’accompagnement.',
  },
];

export const showcase = SHOWCASE.map(({ id, image, blurb }) => {
  const project = projects.find((p) => p.id === id);
  return {
    id,
    image,
    blurb,
    title: project.title,
    context: project.context,
    role: project.role,
    year: project.year,
    colors: project.colors,
    href: `/project/${id}`,
  };
});

export const education = [
  {
    title: 'Mastère Direction artistique et numérique',
    school: 'STUDI, formation à distance',
    period: 'Janvier 2025 à février 2026',
  },
  {
    title: 'BTS Design graphique, médias imprimés',
    school: 'LISAA, Strasbourg',
    period: '2015 à 2017',
  },
  {
    title: 'Mise à niveau en arts appliqués (MANAA)',
    school: 'LISAA, Strasbourg',
    period: '2014',
  },
  {
    title: 'Baccalauréat scientifique, option sciences de l’ingénieur',
    school: 'Lycée Théodore Deck, Guebwiller',
    period: '2013',
  },
];

/** Centres d'intérêt du CV. L'icône renvoie à la table ICONS de Cv.js. */
export const interests = [
  { label: 'Course à pied', icon: 'run' },
  { label: 'Rénovation', icon: 'hammer' },
  { label: 'Illustration digitale', icon: 'pen' },
];

export const contact = {
  pill: 'Contact',
  title: 'Travaillons ensemble',
  intro:
    'Une identité à créer, un support à mettre en page, une campagne à lancer ou un événement à faire connaître ? Écrivez-moi, je réponds vite et je pose beaucoup de questions.',
  email: 'contact@mariemercier.studio',
  phone: '06 47 49 14 78',
  phoneHref: 'tel:+33647491478',
  city: 'Riedisheim, Alsace',
  site: 'www.mariemercier.studio',
  siteHref: 'https://www.mariemercier.studio',
  links: [
    { label: 'Voir le portfolio', href: '/portfolio', internal: true },
    { label: 'Télécharger le CV', href: CV_FILE, download: true },
  ],
};
